import { OperationsRole } from './user.model';
import { PHIDataType } from './audit-log.model';

export enum MeetingType {
  standup = 'standup',
  planning = 'planning',
  retrospective = 'retrospective',
  oneOnOne = 'one_on_one',
  allHands = 'all_hands',
  training = 'training',
  client = 'client',
  other = 'other',
}

export const MeetingTypeLabels: Record<MeetingType, string> = {
  [MeetingType.standup]: 'Stand-up',
  [MeetingType.planning]: 'Planning',
  [MeetingType.retrospective]: 'Retrospective',
  [MeetingType.oneOnOne]: 'One-on-One',
  [MeetingType.allHands]: 'All Hands',
  [MeetingType.training]: 'Training',
  [MeetingType.client]: 'Client Meeting',
  [MeetingType.other]: 'Other',
};

export enum MeetingStatus {
  scheduled = 'scheduled',
  processing = 'processing',
  processed = 'processed',
  failed = 'failed',
  archived = 'archived',
}

export const MeetingStatusLabels: Record<MeetingStatus, string> = {
  [MeetingStatus.scheduled]: 'Scheduled',
  [MeetingStatus.processing]: 'Processing',
  [MeetingStatus.processed]: 'Processed',
  [MeetingStatus.failed]: 'Failed',
  [MeetingStatus.archived]: 'Archived',
};

export interface PHIFlag {
  type: PHIDataType;
  confidence: 'low' | 'medium' | 'high';
  location: string; // "Line 45-48" or "Decision #2"
  detectedText?: string; // Redacted snippet
  recommendedAction: 'review' | 'redact' | 'clear';
}

export interface Meeting {
  id: string;

  // Core Details
  title: string;
  description?: string;
  type: MeetingType;
  status: MeetingStatus;

  // Transcript
  transcriptText: string;
  transcriptUrl?: string; // Storage URL if uploaded as file
  transcriptFileName?: string;
  transcriptFileSize?: number;

  // Processing
  aiProcessedAt?: Date;
  aiProcessingDuration?: number; // milliseconds
  aiModelUsed?: string; // 'claude-sonnet-4.5'
  aiProcessingError?: string; // Error message if failed
  aiProcessingStartedAt?: Date;

  // Meeting Details
  meetingDate: Date;
  attendees: string[]; // Array of names or user IDs
  location?: string;
  duration?: number; // minutes

  // Extracted Counts (calculated)
  decisionsCount: number;
  tasksCount: number;
  risksCount: number;
  openQuestionsCount: number;

  // PHI Detection
  containsPHI: boolean;
  phiFlags: PHIFlag[];

  // Permissions
  accessUserIds: string[]; // Specific users with access
  accessRoles: OperationsRole[]; // Roles with access
  isPublic: boolean; // All operations staff can access

  // Metadata
  tags: string[];
  projectId?: string;
  departmentId?: string;
  customFields?: Record<string, unknown>;

  // Audit
  createdBy: string;
  createdByName: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface ProcessingResult {
  success: boolean;
  decisionsCount: number;
  tasksCount: number;
  risksCount: number;
  questionsCount: number;
  phiDetected: boolean;
  processingTime: number; // milliseconds
  error?: string;
}

export interface ProcessingStatus {
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number; // 0-100
  message: string;
  startedAt?: Date;
  completedAt?: Date;
  error?: string;
}
